import { useState, useCallback } from 'react';
import {
  useRoles,
  useCreateRole,
  useUpdateRole,
  useDeleteRole,
  useBatchUpdateRoleModules,
} from '../../../hooks/useRoles';
import { ITEMS_PER_PAGE, INITIAL_PROFILE_STATE } from '../constants/profilesConstants';
import { filterProfiles } from '../utils/profilesUtils';
import { usePaginatedFilter } from '../../../common/hooks/usePaginatedFilter';

export const useProfilesTable = () => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [accessDialogOpen, setAccessDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState(INITIAL_PROFILE_STATE);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [profileToDelete, setProfileToDelete] = useState(null);

  const { data: profiles = [], isLoading: rolesLoading } = useRoles();

  const createMutation = useCreateRole();
  const updateMutation = useUpdateRole();
  const deleteMutation = useDeleteRole();
  const batchUpdateRoleModulesMutation = useBatchUpdateRoleModules();

  const {
    data,
    totalPages,
    totalCount,
    page,
    rowsPerPage,
    searchTerm,
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
  } = usePaginatedFilter({
    data: profiles,
    itemsPerPage: ITEMS_PER_PAGE,
    filterFn: (collection, term) => filterProfiles(collection, term),
  });

  const isLoading =
    rolesLoading ||
    createMutation.isPending ||
    updateMutation.isPending ||
    deleteMutation.isPending ||
    batchUpdateRoleModulesMutation.isPending;

  const handleOpenDialog = useCallback(() => {
    setSelectedProfile(INITIAL_PROFILE_STATE);
    setEditMode(false);
    setDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setDialogOpen(false);
    setAccessDialogOpen(false);
    setSelectedProfile(INITIAL_PROFILE_STATE);
  }, []);

  const handleInputChange = useCallback((event) => {
    const { name, value } = event.target;
    setSelectedProfile((prev) => ({ ...prev, [name]: value }));
  }, []);

  const handleSave = useCallback(async () => {
    try {
      if (editMode) {
        await updateMutation.mutateAsync(selectedProfile);
      } else {
        await createMutation.mutateAsync(selectedProfile);
      }
      handleCloseDialog();
    } catch (error) {
      console.error('Error saving profile:', error);
    }
  }, [editMode, selectedProfile, updateMutation, createMutation, handleCloseDialog]);

  const handleEdit = useCallback((profile) => {
    setSelectedProfile(profile);
    setEditMode(true);
    setDialogOpen(true);
  }, []);

  const handleDelete = useCallback(
    (uuid) => {
      const profile = profiles.find((p) => p.uuid === uuid);
      setProfileToDelete(profile);
      setDeleteConfirmOpen(true);
    },
    [profiles]
  );

  const handleConfirmDelete = useCallback(async () => {
    if (profileToDelete) {
      try {
        await deleteMutation.mutateAsync(profileToDelete.uuid);
        setDeleteConfirmOpen(false);
        setProfileToDelete(null);
      } catch (error) {
        console.error('Error deleting profile:', error);
      }
    }
  }, [profileToDelete, deleteMutation]);

  const handleCancelDelete = useCallback(() => {
    setDeleteConfirmOpen(false);
    setProfileToDelete(null);
  }, []);

  const handleAccessEdit = useCallback((profile) => {
    setSelectedProfile(profile);
    setAccessDialogOpen(true);
  }, []);

  const handleCheckBoxChange = useCallback(async (uuid, event) => {
    console.warn('handleCheckBoxChange is deprecated, use batch update instead');
  }, []);

  const handleSaveAccess = useCallback(
    async (roleId, selectedModuleUuids) => {
      try {
        await batchUpdateRoleModulesMutation.mutateAsync({
          roleId,
          moduleUuids: selectedModuleUuids,
        });
        handleCloseDialog();
      } catch (error) {
        console.error('Error updating role modules:', error);
      }
    },
    [batchUpdateRoleModulesMutation, handleCloseDialog]
  );

  return {
    data,
    isLoading,
    page,
    totalPages,
    totalCount,
    rowsPerPage,
    searchTerm,
    dialogOpen,
    accessDialogOpen,
    editMode,
    selectedProfile,
    handleSearch,
    handlePageChange,
    handleRowsPerPageChange,
    handleOpenDialog,
    handleCloseDialog,
    handleInputChange,
    handleSave,
    handleEdit,
    handleDelete,
    handleAccessEdit,
    handleCheckBoxChange,
    handleSaveAccess,
    deleteConfirmOpen,
    profileToDelete,
    handleConfirmDelete,
    handleCancelDelete,
  };
};
